import type { ChangeEvent, FC, MouseEvent } from 'react';
import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import classNames from 'classnames';
import { Form, Input, Modal, Spin } from 'antd';
import { Checkbox } from 'antd/lib';
import { Button } from 'common/common_components/Button/Button';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usersActions, usersSelectors } from 'store/slices/users/config';
import { useAppDispatch, useAppSelector } from 'store';
import { UserIcon } from '@heroicons/react/20/solid';
import type { User } from 'types/users';
import { UserRoles } from 'types/users';
import { AppRoutes } from 'common/types';
import queryString from 'query-string';
import { useTranslations } from 'hooks/useTranslations';
import { useCheckMobile } from 'common/helpers/checkIsMobile';
import { EyeVisible } from 'icons/EyeVisible';
import { EyeInvisible } from 'icons/EyeInvisible';
import { LogoutIcon } from 'icons/clientDashboard/Aside/logout/Logout';
import { IconButton } from 'common/common_components/IconButton/IconButton';
import styles from './LoginOrProfile.module.scss';
import { SocialLogin } from './atoms/SocialLogin';

type Credentials = {
  email: string;
  password: string;
};

enum ProfileAttributes {
  'p' = 'p',
  'a' = 'a',
}
type LoginOrProfileProps = {
  label?: string;
  propsClassNames?: Record<Partial<ProfileAttributes>, string>;
};
export const LoginOrProfile: FC<LoginOrProfileProps> = ({
  label,
  propsClassNames,
}) => {
  const { t } = useTranslations();
  const isMobile = useCheckMobile();
  const profile = useAppSelector(usersSelectors.selectProfile) as User;
  const profileIsPending = useAppSelector(
    usersSelectors.selectUsersPendingStatus,
  );
  const isAuthenticated = useAppSelector(usersSelectors.selectUserAuthStatus);
  const isSocialLoginPending = useAppSelector(
    usersSelectors.selectIsSocialLoginPending,
  );
  const [, setQueryParams] = useSearchParams();
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<Credentials>({
    email: '',
    password: '',
  });
  const handleOpenLoginModal = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    setModalIsOpen(true);
  };

  const handleCloseLoginModal = () => {
    setModalIsOpen(false);
    setIsPasswordReset(false);
    setQueryParams({});
    setCredentials({ email: '', password: '' });
  };

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    setCredentials({ ...credentials, [ev.target.name]: ev.target.value });
  };

  const handleSubmit = async () => {
    dispatch(usersActions.resetDraftUser());
    dispatch(usersActions.login(credentials, handleCloseLoginModal));
  };
  const handleNavigate = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    if (!isAuthenticated) {
      return;
    }
    if (profile.role === UserRoles.CLIENT) {
      navigate(`${AppRoutes.CLIENT_DASHBOARD}`);
      return;
    }
    navigate(AppRoutes.ADMIN);
  };

  const handleLogout = useCallback(
    (ev: MouseEvent<HTMLOrSVGElement>) => {
      ev.stopPropagation();
      ev.preventDefault();
      dispatch(usersActions.logout());
      navigate(AppRoutes.HOME);
    },
    [dispatch, navigate],
  );
  const renderButton = useCallback(() => {
    if (profileIsPending) {
      return <Spin />;
    }

    if (label) {
      return (
        <span className="whitespace-nowrap !font-avant-garde">{label}</span>
      );
    }

    if (isMobile) {
      return (
        <div className="flex gap-4">
          <UserIcon width={20} height={20} />
          {isAuthenticated && (
            <IconButton
              icon={<LogoutIcon width={20} height={20} />}
              onClick={handleLogout}
            />
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 ">
        {isAuthenticated ? (
          <p className="flex gap-2.5 mb-0">
            <UserIcon width={20} height={20} />
            <span className="md:hidden">{profile.firstName}</span>

            <IconButton
              className="flex items-center gap-2"
              icon={<LogoutIcon width={20} height={20} />}
              onClick={handleLogout}
              contentClassName="whitespace-nowrap md:hidden"
              content={t('LOGOUT_CONTENT')}
            />
          </p>
        ) : (
          <span className="whitespace-nowrap">{t('MY_ACCOUNT')}</span>
        )}
      </div>
    );
  }, [
    handleLogout,
    isAuthenticated,
    isMobile,
    label,
    profile?.firstName,
    profileIsPending,
    t,
  ]);

  const togglePasswordReset = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    setIsPasswordReset(!isPasswordReset);
  };
  const handleReset = () => {
    dispatch(
      usersActions.resetPassword(credentials.email, handleCloseLoginModal),
    );
  };

  return (
    <div className={styles.login}>
      <Link
        to="#"
        className={classnames('flex justify-center', propsClassNames?.a)}
        onClick={isAuthenticated ? handleNavigate : handleOpenLoginModal}
      >
        <div
          className={classNames(
            styles.loginTextContainer,
            'text-white w-full cursor-pointer text-sm m-0',
            propsClassNames?.p,
          )}
        >
          {renderButton()}
        </div>
      </Link>
      <Modal
        open={
          modalIsOpen ||
          !!queryString.parse(window.location.search).openLoginPopup
        }
        closeIcon={false}
        footer={null}
        onCancel={handleCloseLoginModal}
      >
        <>
          <h1 className={styles.modalTitle}>
            {isPasswordReset ? 'Reset password' : 'Login'}
          </h1>
          <Form
            layout="vertical"
            //@ts-ignore
            onChange={handleChange}
          >
            <Form.Item label="Email Address*" rules={[{ required: true }]}>
              <Input
                placeholder="Write your email please"
                name="email"
                value={credentials.email}
                disabled={isSocialLoginPending}
              />
            </Form.Item>
            {!isPasswordReset && (
              <Form.Item label="Password*" rules={[{ required: true }]}>
                <Input.Password
                  value={credentials.password}
                  name="password"
                  onChange={handleChange}
                  disabled={isSocialLoginPending}
                  placeholder="Please write your password"
                  iconRender={(visible) =>
                    visible ? <EyeVisible /> : <EyeInvisible />
                  }
                />
              </Form.Item>
            )}

            <div className="flex justify-between items-center">
              {!isPasswordReset && (
                <Checkbox className="whitespace-nowrap">
                  {t('REMEMBER_ME')}
                </Checkbox>
              )}
              <Link
                to="/"
                onClick={togglePasswordReset}
                className="hover:text-orange text-black whitespace-nowrap"
              >
                {t(isPasswordReset ? 'BACK_TO_LOGIN' : 'FORGOT_PASSWORD')}
              </Link>
              <div className="flex flex-col gap-3">
                <Button
                  isLoading={profileIsPending}
                  content={isPasswordReset ? 'Send email' : 'LOG IN'}
                  contentClassName={styles.ctaContent}
                  className={styles.cta}
                  onClick={isPasswordReset ? handleReset : handleSubmit}
                />

                {!isPasswordReset && (
                  <SocialLogin callback={handleCloseLoginModal} />
                )}
              </div>
            </div>
          </Form>
        </>
      </Modal>
    </div>
  );
};
